import { createFileRoute, Link } from "@tanstack/react-router";
import { useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Phone, KeyRound, CheckCircle2, XCircle, ArrowLeft, Loader2 } from "lucide-react";
import { getMyVoysId, updateMyVoysId, testVoysToken, voysClickToDial } from "@/lib/voys.functions";

export const Route = createFileRoute("/_authenticated/medewerker/voys-instellingen")({
  head: () => ({
    meta: [
      { title: "Voys-instellingen — Elvion Office" },
      { name: "description", content: "Interne beheerpagina voor medewerkers van Elvion Group Nederland om hun Voys click-to-dial koppeling in te stellen en te testen. Alleen toegankelijk na inloggen." },
      { name: "robots", content: "noindex, nofollow" },
    ],
  }),
  component: VoysSettingsPage,
});

function VoysSettingsPage() {
  const qc = useQueryClient();
  const getFn = useServerFn(getMyVoysId);
  const saveFn = useServerFn(updateMyVoysId);
  const testTokenFn = useServerFn(testVoysToken);
  const dialFn = useServerFn(voysClickToDial);

  const { data, isLoading } = useQuery({
    queryKey: ["my-voys-id"],
    queryFn: () => getFn({}),
  });

  const [voysUserId, setVoysUserId] = useState("");
  const [testNumber, setTestNumber] = useState("");
  const [tokenResult, setTokenResult] = useState<any>(null);

  useEffect(() => {
    if (data?.voysUserId != null) setVoysUserId(data.voysUserId);
  }, [data?.voysUserId]);

  const save = useMutation({
    mutationFn: () => saveFn({ data: { voysUserId: voysUserId.trim() || null } }),
    onSuccess: (r: any) => {
      if (r?.ok === false) {
        toast.error(`Opslaan geweigerd: ${r.detail ?? r.reason}`);
        return;
      }
      if (r?.validation?.found) toast.success("Voys-id gevonden en opgeslagen");
      else toast.success("Voys-gebruikers-id opgeslagen");
      qc.invalidateQueries({ queryKey: ["my-voys-id"] });
    },
    onError: (e: any) => toast.error(e?.message ?? "Opslaan mislukt"),
  });

  const testToken = useMutation({
    mutationFn: () => testTokenFn({}),
    onSuccess: (r: any) => {
      setTokenResult(r);
      if (r.ok) toast.success("Voys-token werkt");
      else toast.error(`Token-test: ${r.detail ?? r.reason}`);
    },
    onError: (e: any) => toast.error(e?.message ?? "Test mislukt"),
  });

  const testCall = useMutation({
    mutationFn: () => dialFn({ data: { phoneNumber: testNumber } }),
    onSuccess: (r: any) => {
      if (r.ok) toast.success(`Belverzoek verstuurd: ${r.bNumber}`);
      else toast.error(r.detail ?? r.reason);
    },
    onError: (e: any) => toast.error(e?.message ?? "Bellen mislukt"),
  });

  const tokenConfigured = data?.configured ?? false;

  return (
    <div className="container max-w-3xl mx-auto py-8 px-4 space-y-6">
      <div className="flex items-center gap-3">
        <Button asChild variant="ghost" size="sm">
          <Link to="/medewerker/office">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Terug naar Office
          </Link>
        </Button>
      </div>

      <div>
        <h1 className="text-3xl font-bold tracking-tight">Voys-instellingen</h1>
        <p className="text-muted-foreground mt-1">
          Beheer de koppeling met de Voys/VoIPGRID click-to-dial API.
        </p>
      </div>

      {/* API TOKEN */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <KeyRound className="w-5 h-5" />
            VOYS_API_TOKEN
          </CardTitle>
          <CardDescription>
            Organisatiebreed Freedom API-token. Wordt versleuteld opgeslagen in Lovable Cloud
            en is nooit zichtbaar in de browser.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center gap-2">
            <span className="text-sm">Status:</span>
            {tokenConfigured ? (
              <Badge className="bg-emerald-600 hover:bg-emerald-600">
                <CheckCircle2 className="w-3 h-3 mr-1" /> Ingesteld
              </Badge>
            ) : (
              <Badge variant="destructive">
                <XCircle className="w-3 h-3 mr-1" /> Ontbreekt
              </Badge>
            )}
          </div>

          <Alert>
            <AlertTitle>Token bijwerken of vervangen</AlertTitle>
            <AlertDescription className="space-y-2">
              <p>
                Om veiligheidsredenen kunnen tokens alleen via het beveiligde Lovable-secrets-formulier
                worden ingevoerd — niet via een gewoon invoerveld in de app.
              </p>
              <p>
                Vraag in de Lovable-chat: <em>"Werk VOYS_API_TOKEN bij"</em>. Er verschijnt dan een
                veilig formulier waar je de nieuwe waarde plakt.
              </p>
            </AlertDescription>
          </Alert>

          <div className="flex gap-2">
            <Button
              variant="outline"
              onClick={() => testToken.mutate()}
              disabled={!tokenConfigured || testToken.isPending}
            >
              {testToken.isPending && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Test token
            </Button>
          </div>

          {tokenResult && (
            <div className="text-sm rounded-md border p-3 bg-muted/40 space-y-1">
              {tokenResult.ok ? (
                <>
                  <div className="text-emerald-700 font-medium">✓ Token geldig ({tokenResult.stage})</div>
                  {tokenResult.client && <div>Client: <code>{tokenResult.client}</code></div>}
                  {tokenResult.user && <div>Gebruiker: <code>{tokenResult.user}</code></div>}
                  <div>
                    Click-to-dial rechten:{" "}
                    {tokenResult.clickToDialOk ? (
                      <span className="text-emerald-700">✓ OK</span>
                    ) : (
                      <span className="text-amber-700">⚠ {tokenResult.clickToDialDetail}</span>
                    )}
                  </div>
                  {tokenResult.tokenMeta && (
                    <div className="text-xs text-muted-foreground">
                      Token: {tokenResult.tokenMeta.length} tekens · {tokenResult.tokenMeta.prefix}…{tokenResult.tokenMeta.suffix}
                    </div>
                  )}
                </>
              ) : (
                <>
                  <div className="text-destructive font-medium">
                    ✗ {tokenResult.stage === "auth" ? "Authenticatie mislukt" : tokenResult.stage === "config" ? "Configuratie mist" : "Fout"} — {tokenResult.reason}
                  </div>
                  {tokenResult.status && <div>HTTP-status: <code>{tokenResult.status}</code></div>}
                  {tokenResult.detail && <div className="text-muted-foreground">{tokenResult.detail}</div>}
                  {tokenResult.tokenMeta && (
                    <div className="text-xs text-muted-foreground">
                      Token: {tokenResult.tokenMeta.length} tekens · {tokenResult.tokenMeta.prefix}…{tokenResult.tokenMeta.suffix}
                    </div>
                  )}
                </>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* VOYS USER ID */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Phone className="w-5 h-5" />
            Mijn Voys-gebruikers-id
          </CardTitle>
          <CardDescription>
            Persoonlijke interne extension of user-id in Voys. Wordt gebruikt als <code>a_number</code>{" "}
            bij click-to-dial: eerst rinkelt jouw toestel, daarna wordt verbonden met de klant.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="voys-user-id">Voys user-id / extension</Label>
            <Input
              id="voys-user-id"
              value={voysUserId}
              onChange={(e) => setVoysUserId(e.target.value)}
              placeholder="bv. 201 of user-uuid"
              disabled={isLoading}
              maxLength={64}
            />
            <p className="text-xs text-muted-foreground">
              Vind je in je Voys Freedom-portaal onder Gebruikers → jouw account.
            </p>
          </div>
          <Button onClick={() => save.mutate()} disabled={save.isPending || isLoading}>
            {save.isPending && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
            Opslaan
          </Button>
        </CardContent>
      </Card>

      {/* TEST CALL */}
      <Card>
        <CardHeader>
          <CardTitle>Testoproep</CardTitle>
          <CardDescription>
            Doe een click-to-dial naar een nummer om end-to-end te controleren.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="test-number">Telefoonnummer</Label>
            <Input
              id="test-number"
              value={testNumber}
              onChange={(e) => setTestNumber(e.target.value)}
              placeholder="+31612345678"
              maxLength={32}
            />
          </div>
          <Button
            onClick={() => testCall.mutate()}
            disabled={!tokenConfigured || !voysUserId.trim() || !testNumber.trim() || testCall.isPending}
          >
            {testCall.isPending && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
            <Phone className="w-4 h-4 mr-2" />
            Bel nu
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}
